import * as React from 'react';
import { FC, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { getLocalizedServiceMode } from 'utils/service-mode';

import {
  ReceiptDate,
  ReceiptHeadInfoContainer,
  ReceiptInfo,
  ReceiptInfoDetail,
  ReceiptInfoDetailLabel,
  ReceiptInfoLabel,
} from './receipt-head-info.styled';
import { IOrderHeaderProps } from './types';

const ReceiptHeadInfo: FC<IOrderHeaderProps> = ({ order }) => {
  const { formatMessage, formatTime } = useIntl();
  const { pickupDate } = useOrderTimedFire({ serverOrder: order });
  const { formattedDate, formattedTime } = useMemo(() => {
    const orderDate = new Date(order.createdAt);
    const formattedOrderDate = orderDate.toLocaleString(formatMessage({ id: 'localeDateTime' }), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const formattedOrderTime = orderDate.toLocaleString('en-us', {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });

    return { formattedDate: formattedOrderDate, formattedTime: formattedOrderTime };
  }, [formatMessage, order.createdAt]);

  const { storeAddress, serviceMode, ticketNumber } = order.cart;
  const localizedServiceMode = getLocalizedServiceMode(serviceMode, formatMessage);

  return (
    <ReceiptHeadInfoContainer>
      <ReceiptInfo>
        <ReceiptDate>{formattedDate}</ReceiptDate>
      </ReceiptInfo>

      <ReceiptInfo>
        <ReceiptInfoLabel>{formatMessage({ id: 'orderNumber' })}:</ReceiptInfoLabel>
        <ReceiptInfoDetail>{ticketNumber}</ReceiptInfoDetail>
      </ReceiptInfo>

      <ReceiptInfo>
        <ReceiptInfoLabel>{formatMessage({ id: 'orderTime' })}:</ReceiptInfoLabel>
        <ReceiptInfoDetail>
          <ReceiptInfoDetailLabel>{formattedTime}</ReceiptInfoDetailLabel>
        </ReceiptInfoDetail>
      </ReceiptInfo>

      <ReceiptInfo>
        <ReceiptInfoLabel>
          {formatMessage(
            { id: 'method' },
            {
              serviceMode: '', // set service mode below for proper formatting in grid
            }
          )}
        </ReceiptInfoLabel>
        <ReceiptInfoDetail>
          <ReceiptInfoDetailLabel>{localizedServiceMode}</ReceiptInfoDetailLabel>
        </ReceiptInfoDetail>
      </ReceiptInfo>

      {!!order.fireOrderIn && (
        <ReceiptInfo>
          <ReceiptInfoLabel>{formatMessage({ id: 'pickUpTime' })}:</ReceiptInfoLabel>
          <ReceiptInfoDetail>
            <ReceiptInfoDetailLabel>{formatTime(pickupDate)}</ReceiptInfoDetailLabel>
          </ReceiptInfoDetail>
        </ReceiptInfo>
      )}

      <ReceiptInfo>
        <ReceiptInfoLabel>{formatMessage({ id: 'location' })}:</ReceiptInfoLabel>
        <ReceiptInfoDetail>
          <ReceiptInfoDetailLabel>{storeAddress.addressLine1}</ReceiptInfoDetailLabel>
          {storeAddress.addressLine2 && (
            <ReceiptInfoDetailLabel>
              {formatMessage({ id: 'unitNumber' }, { unitNumber: storeAddress.addressLine2 })}
            </ReceiptInfoDetailLabel>
          )}
          <ReceiptInfoDetailLabel>{storeAddress.city}</ReceiptInfoDetailLabel>
          <ReceiptInfoDetailLabel>{storeAddress.state}</ReceiptInfoDetailLabel>
          <ReceiptInfoDetailLabel>{storeAddress.zip}</ReceiptInfoDetailLabel>
        </ReceiptInfoDetail>
      </ReceiptInfo>
    </ReceiptHeadInfoContainer>
  );
};

export default ReceiptHeadInfo;
